import {
  CloseCircleOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, DatePicker, Input, Row, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  useLazyGetOperationsFiltersDataQuery,
  useLazyGetOperationsSearchResultQuery,
  useSearchOperationsMutation,
} from '@app/api/ClientPayments';
import { Error403Unauthorized, MultipleSelect } from '@app/components';
import {
  DATE_FORMATS,
  DATE_PICKER_FORMAT,
  MIN_THREE_SYMBOLS,
  MOBILE,
  OPERATIONS,
  PATTERN_SEARCH_FIELD_ID,
  PREFIX,
  rangePickerLocale,
  today,
  WARNING,
} from '@app/constants';
import {
  useContentWidth,
  useMemoizeObject,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { useRegistryFilterState } from '@app/shared/hooks';
import type { NSmallGlobalEntities } from '@app/types';
import type { NClientPayments } from '@app/types/ClientPayments';
import type { NLegalEntities } from '@app/types/LegalEntities';
import type { NUsers } from '@app/types/Users';
import { changeStandardPaginationUi } from '@app/utils';

import { FO_PAY_TYPES } from '@pages/Finances/FinancialOperations/constants/payTypes';

import { useClientPaymentsList } from '@pages/Clients//ClientPayments/ClientPaymentsList/hooks/useClientPaymentsList';

import { FinancialOperationsTable } from '@pages/Finances/FinancialOperations/components/FinancialOperationsTable';

import { defaultSearchParams } from '@pages/Finances/FinancialOperations/data/defaultSearchParams';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { BaseOptionType } from 'antd/es/select';

import '@pages/Finances/FinancialOperations/styles/FinancialOperations.desktop.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const FinancialOperations = () => {
  const { activeBranchId } = useUserPrint();
  const [getOperationsFiltersData] = useLazyGetOperationsFiltersDataQuery();
  const [getOperationsSearchResult] = useLazyGetOperationsSearchResultQuery();
  const [searchOperations] = useSearchOperationsMutation();
  const { hasUserRightForModuleView } = useClientPaymentsList();
  const { contentWidth } = useContentWidth();
  const { showNotification } = useContext(NotificationContext);
  const [dictionaries, setDictionaries] =
    useState<NClientPayments.ModelClientPaymentFiltersData | null>(null);
  const [filtersLoader, setFiltersLoader] = useState(false);
  // Выбранные фильтры переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161): хранилище вкладочное, как у реестра
  // записей и реестра задач.
  const [selectedUsers, setSelectedUsers] = useRegistryFilterState<
    BaseOptionType[] | null
  >('finances', 'selectedUsers', null);
  const [selectedPayTypes, setSelectedPayTypes] = useRegistryFilterState<
    BaseOptionType[] | null
  >('finances', 'selectedPayTypes', null);
  const [selectedPaymentAccounts, setSelectedPaymentAccounts] =
    useRegistryFilterState<BaseOptionType[] | null>(
      'finances',
      'selectedPaymentAccounts',
      null
    );
  const [selectedLegalEntities, setSelectedLegalEntities] =
    useRegistryFilterState<BaseOptionType[] | null>(
      'finances',
      'selectedLegalEntities',
      null
    );
  const [searchParams, setSearchParams] = useState(defaultSearchParams);
  const yesterday = dayjs(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const [datesRange, setDatesRange] = useState<NSmallGlobalEntities.RangeValue>(
    [yesterday, today]
  );
  const [operationsList, setOperationsList] =
    useState<NClientPayments.ModelOperationSearchResult['Items']>(null);
  const [searchButtonLoader, setSearchButtonLoader] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [resetButtonLoader, setResetButtonLoader] = useState(false);
  const [searchQueryError, setSearchQueryError] = useState<
    typeof MIN_THREE_SYMBOLS | null
  >(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { innerWidth } = useWindowSize();
  const [areFiltersOpened, setFiltersOpening] = useState<boolean>(
    innerWidth > 834
  );
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [searchId, setSearchId] = useState<number | null>(null);

  const memoizedSearchParams = useMemoizeObject(searchParams);

  const handleTableChange = async (page: number, pageSize: number) => {
    if (searchId) {
      setTableLoader(true);

      try {
        const result = await getOperationsSearchResult({
          ActiveBranchId: activeBranchId,
          searchId,
          page: pageSize === rowsPerPage ? page : 1,
          rowsPerPage: pageSize,
        }).unwrap();

        setOperationsList(result?.Items || []);
        setRowsPerPage(pageSize);
        setPageNumber(result.PageN);
      } catch (error) {
        showNotification((error as Error).message);
      } finally {
        setTableLoader(false);
      }
    }
  };

  const isMobile = innerWidth <= MOBILE;

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
    });
  }, [operationsList, isMobile]);

  useEffect(() => {
    const fetchOperations = async () => {
      setTableLoader(true);

      try {
        const data = await searchOperations({
          ActiveBranchId: activeBranchId,
          data: memoizedSearchParams || defaultSearchParams,
        }).unwrap();

        setOperationsList(data?.Items || []);
        setTotalRowsCount(data?.CountItems || 0);
        setSearchId(data?.Id || null);
        setPageNumber(1);
      } catch (error) {
        showNotification((error as Error).message, WARNING);
      } finally {
        setTableLoader(false);
      }
    };

    fetchOperations();
  }, [
    activeBranchId,
    memoizedSearchParams,
    searchOperations,
    showNotification,
  ]);

  useEffect(() => {
    const fetchOperationsFiltersData = async () => {
      setFiltersLoader(true);

      try {
        const data = await getOperationsFiltersData({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setDictionaries(data);
      } catch (error) {
        showNotification((error as Error).message, WARNING);
      } finally {
        setFiltersLoader(false);
      }
    };

    fetchOperationsFiltersData();
  }, [activeBranchId, getOperationsFiltersData, showNotification]);

  const runSearch = useCallback(async () => {
    setSearchButtonLoader(true);

    try {
      const data = await searchOperations({
        ActiveBranchId: activeBranchId,
        data: {
          ...searchParams,
          SearchTerm: searchQuery || null,
          DateS: dayjs(datesRange?.[0], 'YYYY-MM-DD').format(
            DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
          ),
          DateE: dayjs(datesRange?.[1], 'YYYY-MM-DD').format(
            DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
          ),
          UserIds:
            (selectedUsers?.map(({ value }) => value)?.length || 0) > 0
              ? (selectedUsers?.map(({ value }) => value) as Array<
                  NUsers.User['id']
                >)
              : null,
          PaymentAccountIds:
            (selectedPaymentAccounts?.map(({ value }) => value)?.length || 0) >
            0
              ? (selectedPaymentAccounts?.map(({ value }) => value) as Array<
                  NUsers.User['id']
                >)
              : null,
          PayTypes:
            (selectedPayTypes?.map(({ value }) => value)?.length || 0) > 0
              ? (selectedPayTypes?.map(
                  ({ value }) => value
                ) as Array<NClientPayments.PayType>)
              : null,
          LegalEntityIds:
            (selectedLegalEntities?.map(({ value }) => value)?.length || 0) > 0
              ? (selectedLegalEntities?.map(({ value }) => value) as Array<
                  NLegalEntities.ModelLegalEntity['Id']
                >)
              : null,
        },
      }).unwrap();

      setOperationsList(data?.Items || []);
      setTotalRowsCount(data?.CountItems || 0);
      setSearchId(data?.Id || null);
      setPageNumber(1);
    } catch (error) {
      showNotification((error as Error).message, WARNING);
    } finally {
      setSearchButtonLoader(false);
    }
  }, [
    activeBranchId,
    searchParams,
    searchQuery,
    datesRange,
    selectedUsers,
    selectedPaymentAccounts,
    selectedPayTypes,
    selectedLegalEntities,
    searchOperations,
    showNotification,
  ]);

  const resetFilters = useCallback(async () => {
    try {
      setSearchParams(defaultSearchParams);

      setDatesRange([yesterday, today]);
      setSearchQuery('');
      setSelectedUsers([]);
      setSelectedPayTypes([]);
      setSelectedPaymentAccounts([]);
      setSelectedLegalEntities([]);

      setResetButtonLoader(true);

      const data = await searchOperations({
        ActiveBranchId: activeBranchId,
        data: searchParams,
      }).unwrap();

      setOperationsList(data?.Items || []);
      setTotalRowsCount(data?.CountItems || 0);
      setSearchId(data?.Id || null);
      setPageNumber(1);
    } catch (error) {
      showNotification((error as Error).message, WARNING);
    } finally {
      setResetButtonLoader(false);
    }
  }, [
    yesterday,
    activeBranchId,
    searchParams,
    searchOperations,
    showNotification,
    setSelectedLegalEntities,
    setSelectedPayTypes,
    setSelectedPaymentAccounts,
    setSelectedUsers,
  ]);

  const memoizedFilterGroupButtons = useMemo(() => {
    return (
      <>
        {contentWidth <= 410 && (
          <>
            {areFiltersOpened ? (
              <Button
                block
                icon={
                  <FontAwesomeIcon
                    icon={'fa-solid fa-chevron-up' as IconProp}
                  />
                }
                onClick={() => setFiltersOpening(!areFiltersOpened)}
              >
                Скрыть фильтры
              </Button>
            ) : (
              <Button
                block
                type="primary"
                icon={
                  <FontAwesomeIcon
                    icon={'fa-solid fa-chevron-down' as IconProp}
                  />
                }
                onClick={() => setFiltersOpening(!areFiltersOpened)}
              >
                Показать фильтры
              </Button>
            )}
          </>
        )}
      </>
    );
  }, [areFiltersOpened, contentWidth]);

  const memoizedFiltersOpening = useMemo(() => {
    const isDesktop = contentWidth > 410;

    if (isDesktop) {
      return true;
    } else {
      return areFiltersOpened;
    }
  }, [contentWidth, areFiltersOpened]);

  if (!hasUserRightForModuleView) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={PREFIX + OPERATIONS}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={16} align="bottom">
          {memoizedFilterGroupButtons}

          {/* Начало скрытия фильтров на планшетах и мобильных */}
          {memoizedFiltersOpening && (
            <>
              <Col
                className="gutter-row filters-without-buttons"
                span={contentWidth > 1045 ? 16 : 24}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space
                    className="filters-without-buttons"
                    direction={contentWidth > 825 ? 'horizontal' : 'vertical'}
                    align="start"
                    style={{ width: '100%' }}
                  >
                    <Space
                      className="fo-period-container"
                      align={contentWidth > 415 ? 'center' : undefined}
                      direction={contentWidth > 415 ? 'horizontal' : 'vertical'}
                      style={{ width: contentWidth > 410 ? '408px' : '100%' }}
                    >
                      <p
                        style={{
                          marginTop: 0,
                          marginBottom: 0,
                          minWidth: '57px',
                        }}
                      >
                        Период:
                      </p>
                      <RangePicker
                        classNames={{ popup: { root: 'calendars' } }}
                        locale={rangePickerLocale}
                        allowClear={false}
                        format={DATE_PICKER_FORMAT}
                        style={{
                          width: contentWidth > 415 ? '342px' : '100%',
                        }}
                        value={datesRange}
                        onChange={(values) =>
                          setDatesRange(
                            values as NSmallGlobalEntities.RangeValue
                          )
                        }
                      />
                    </Space>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input
                        placeholder="Введите имя, ID или телефон клиента"
                        addonBefore={<SearchOutlined />}
                        status={searchQueryError ? 'error' : undefined}
                        style={{
                          marginBottom: contentWidth > 855 ? '-8px' : 'auto',
                          width: contentWidth > 410 ? '408px' : '100%',
                        }}
                        value={searchQuery}
                        onChange={(event) => {
                          const value = event.target.value || '';

                          setSearchQuery(event.target.value);

                          if (
                            value &&
                            value.length < 3 &&
                            !value.match(PATTERN_SEARCH_FIELD_ID)
                          ) {
                            setSearchQueryError(MIN_THREE_SYMBOLS);
                          } else {
                            setSearchQueryError(null);
                          }
                        }}
                      />
                      {searchQueryError && (
                        <Text type="danger">{MIN_THREE_SYMBOLS}</Text>
                      )}
                    </Space>
                  </Space>
                  <Space
                    direction={contentWidth > 825 ? 'horizontal' : 'vertical'}
                    style={{ width: '100%' }}
                  >
                    <Space
                      direction={contentWidth > 410 ? 'horizontal' : 'vertical'}
                      style={{ width: '100%' }}
                    >
                      <MultipleSelect
                        loader={filtersLoader}
                        placeholder="Все пользователи"
                        allOptions={(dictionaries?.Users || []).map(
                          ({ Id, Name }) => ({
                            value: Id,
                            label: Name,
                          })
                        )}
                        selectedOption={selectedUsers}
                        setSelectedOptions={setSelectedUsers}
                        minWidth={contentWidth > 410 ? '200px' : '100%'}
                      />
                      <MultipleSelect
                        loader={filtersLoader}
                        placeholder="Все виды оплат"
                        allOptions={FO_PAY_TYPES}
                        selectedOption={selectedPayTypes}
                        setSelectedOptions={setSelectedPayTypes}
                        minWidth={contentWidth > 410 ? '200px' : '100%'}
                      />
                    </Space>
                    <Space
                      direction={contentWidth > 410 ? 'horizontal' : 'vertical'}
                      style={{ width: '100%' }}
                    >
                      <MultipleSelect
                        loader={filtersLoader}
                        placeholder="Все кассы"
                        allOptions={(dictionaries?.PaymentAccounts || []).map(
                          ({ Id, Name }) => ({
                            value: Id,
                            label: Name,
                          })
                        )}
                        selectedOption={selectedPaymentAccounts}
                        setSelectedOptions={setSelectedPaymentAccounts}
                        minWidth={contentWidth > 410 ? '200px' : '100%'}
                      />
                      <MultipleSelect
                        loader={filtersLoader}
                        placeholder="Все юридические лица"
                        allOptions={(dictionaries?.LegalEntities || []).map(
                          ({ Id, Name }) => ({
                            value: Id,
                            label: Name,
                          })
                        )}
                        selectedOption={selectedLegalEntities}
                        setSelectedOptions={setSelectedLegalEntities}
                        minWidth={contentWidth > 410 ? '200px' : '100%'}
                      />
                    </Space>
                  </Space>
                </Space>
              </Col>
              <Col className="gutter-row" span={contentWidth > 1045 ? 8 : 24}>
                <Row
                  justify={contentWidth > 1080 ? 'end' : 'start'}
                  align="bottom"
                >
                  <Space
                    direction={contentWidth > 410 ? 'horizontal' : 'vertical'}
                    style={{ width: contentWidth > 410 ? 'auto' : '100%' }}
                  >
                    <Button
                      block
                      loading={resetButtonLoader}
                      icon={<CloseCircleOutlined />}
                      onClick={resetFilters}
                    >
                      Сброс
                    </Button>
                    <Button
                      block
                      loading={searchButtonLoader}
                      icon={<FilterOutlined />}
                      type="primary"
                      disabled={!!searchQueryError}
                      onClick={runSearch}
                    >
                      Поиск
                    </Button>
                  </Space>
                </Row>
              </Col>
            </>
          )}
          {/* Конец скрытия фильтров на планшетах и мобильных */}
        </Row>
        <FinancialOperationsTable
          handleTableChange={handleTableChange}
          pageNumber={pageNumber}
          isMobile={isMobile}
          tableLoader={tableLoader}
          operationsList={operationsList}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalRowsCount={totalRowsCount}
        />
      </Space>
    </div>
  );
};
